import {hideBackgroundFor} from "@/util/a11yModal"
import {prepareLiveRegions} from "@/util/liveAnnouncer"

const FOCUSABLE = [
    "a[href]", "button:not([disabled])", "input:not([disabled]):not([type=\"hidden\"])",
    "select:not([disabled])", "textarea:not([disabled])", "[tabindex]:not([tabindex=\"-1\"])",
].join(",")
// 이름 없는 제목에 붙이는 id 번호
let titleSeq = 0

/**
 * 모달 위에 띄운 모달이 닫히면 아래 모달의 초점 가두기를 다시 켠다(ACCESSIBILITY.md 4, 11/04 점검).
 * bootstrap 5.3.2 FocusTrap.activate() 는 문서에 단 가두기 처리를 모두 떼고(EventHandler.off(document, ".bs.focustrap")) 자기 것만 단다.
 * 아래 모달은 「켜져 있음」으로 남아 다시 달지 않아, 위 창을 닫은 뒤 Tab 이 감춰진 배경으로 나갔다
 * (ImageEdgeDialog 단독 위 자르기 창, PreviewModal 위 기기·업데이트 창). 발표 창은 restorePresentationFocus 가 스스로 한다.
 *  - 남은 모달이 하나일 때만 켠다. 여럿이 겹쳐 있으면(발표 창 위 기기 연결 위 펌웨어) 맨 위를 알 수 없어 그 화면에 맡긴다.
 *  - 잠긴(inert) 모달, 가두기를 일부러 끈 모달(focus: false — 수식 창·AI 촉각화 창)은 건드리지 않는다.
 *  - 다시 켤 때 자동 초점은 끈다. 켜면 창 자체로 초점이 뛰어, 연 자리로 돌아간 초점(attachModalA11y)을 빼앗는다.
 *
 * @param {HTMLElement} closingEl 닫히는 모달
 * @param {(el: HTMLElement) => any} getInstance bootstrap Modal.getInstance
 * @returns {boolean} 다시 켰는가
 */
export function rearmParentFocusTrap(closingEl, getInstance) {
    const open = Array.from(document.querySelectorAll(".modal.show"))
        .filter(el => el !== closingEl && !el.closest("[inert]"))
    if (open.length !== 1) return false
    const instance = getInstance?.(open[0])
    const trap = instance?._focustrap
    if (!trap?._config || instance._config?.focus === false) return false
    // 보이지 않는 인스턴스(다른 bootstrap 사본이 연 창을 data-bs-dismiss 가 getOrCreateInstance 로 새로 만든 것)나 여닫는 중인 창은
    // 켜지 않는다. 켜면 hide() 가 끄지 못해 가두기가 남는다(f5 11/04 검토 M4).
    if (!instance._isShown || instance._isTransitioning) return false
    const autofocus = trap._config.autofocus
    trap._config.autofocus = false
    try {
        trap.deactivate()
        trap.activate()
    } finally {
        trap._config.autofocus = autofocus
    }
    return true
}

const isShown = el => Boolean(el.offsetWidth || el.offsetHeight || el.getClientRects().length)

/**
 * 열린 창의 마지막 요소에서 Tab, 첫 요소·창 자체에서 Shift+Tab 을 창 안으로 돌린다(ACCESSIBILITY.md 4).
 * bootstrap FocusTrap 과 activateDialogA11y 는 focusin 만 본다. 창보다 DOM 뒤에 초점 받을 요소가 없으면 Tab 이 브라우저(주소창)로
 * 나가 focusin 이 생기지 않아, 초점이 문서 밖으로 나갔다(04 세션 11/04 9079 확인: 설정 창 「변경 사항 저장」 → Tab → body).
 *  - 창이 스스로 Tab 을 막았으면(defaultPrevented) 건드리지 않는다. 문서 버블에서 받으므로 창 안 처리가 먼저 돈다.
 *  - 확인 창(SweetAlert2)은 스스로 돌린다.
 */
export function wrapDialogTab(event) {
    if (event.key !== "Tab" || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
    const active = document.activeElement
    const dialog = active?.closest?.('.modal.show, [role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]')
    if (!dialog || dialog.closest(".swal2-container") || dialog.closest("[inert]")) return
    const items = Array.from(dialog.querySelectorAll(FOCUSABLE))
        .filter(el => !el.closest('[inert], [aria-hidden="true"], [hidden]') && isShown(el))
    if (!items.length) {
        event.preventDefault()
        if (dialog !== active) dialog.focus?.({preventScroll: true})
        return
    }
    const first = items[0]
    const last = items[items.length - 1]
    if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus({preventScroll: true})
    } else if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus({preventScroll: true})
    }
}

let rearmInstalled = false
/**
 * 문서의 hide.bs.modal 을 받아 rearmParentFocusTrap 을 건다. 앱 시작 때 한 번(main.js).
 * hide 이벤트 바로 뒤 bootstrap 이 닫는 모달의 가두기를 끄고 show 를 떼므로, 그 뒤(다음 작업)에 본다.
 */
export function installFocusTrapRearm(getInstance) {
    if (rearmInstalled || typeof document === "undefined") return
    rearmInstalled = true
    document.addEventListener("keydown", wrapDialogTab)
    document.addEventListener("hide.bs.modal", event => {
        const closing = event.target
        setTimeout(() => {
            if (closing?.classList?.contains("show")) return     // 닫기를 막았다
            rearmParentFocusTrap(closing, getInstance)
        }, 0)
    })
}

/**
 * 창 이름이 될 제목을 창 이름으로 잇고, 보이는 제목은 읽기에서 감춘다(ACCESSIBILITY.md 1, 11/04 사용자 결정 — DCW-2045·2049 를 모든 창으로).
 * 초점을 창 자체에 두면 NVDA 는 창 이름을 읽고 「페이지 로드 시 자동으로 모두 읽기」(기본 켬)로 초점 자리부터 이어 읽는다
 * (browseMode.event_treeInterceptor_gainFocus). 보이는 제목까지 읽히면 제목이 두 번 들린다.
 * aria-labelledby 가 직접 가리키는 요소는 aria-hidden 이어도 이름 계산에 들어간다(accname).
 * 창에 aria-label 이 있으면 그것이 이름이라 제목은 건드리지 않는다. 제목이 없으면 아무것도 하지 않는다(창마다 이름을 줘야 한다).
 *
 * @param {HTMLElement} dialogEl
 * @returns {HTMLElement|null} 창 이름이 된 제목
 */
export function nameDialogByTitle(dialogEl) {
    if (!dialogEl || dialogEl.hasAttribute("aria-label")) return null
    const labelledBy = dialogEl.getAttribute("aria-labelledby")
    const byId = labelledBy && dialogEl.querySelector(`#${CSS.escape(labelledBy.split(/\s+/)[0])}`)
    // 이름이 없으면 머리말의 제목만 이름으로 잡는다. 본문의 첫 제목(h1~h3)은 내용이라 잡지 않는다.
    const title = byId || (!labelledBy && dialogEl.querySelector(".modal-title, .modal-header h1, .modal-header h2, .modal-header h3"))
    if (!title) return null
    if (!labelledBy) {
        if (!title.id) title.id = `dialog-title-${++titleSeq}`
        dialogEl.setAttribute("aria-labelledby", title.id)
    }
    // 감추지 않는 제목:
    //  - 초점을 받게 만든 제목(tabindex 가 있거나 지금 초점이 있다). QA 가 「열면 제목에 초점」을 요구한 창이 있다
    //    (DCW-1999 이미지 창 단계 제목, 뷰어의 tabindex 제목). 감추면 초점 받은 제목이 읽히지 않는다(11/04 회귀).
    //  - 안에 누를 것이 든 제목(감춘 곳에 초점이 갈 수 있게 되면 안 된다).
    const focusTarget = title.hasAttribute("tabindex") || title === document.activeElement || title.contains(document.activeElement)
    if (!focusTarget && !title.querySelector(FOCUSABLE)) title.setAttribute("aria-hidden", "true")
    return title
}

// 창 자체에 초점을 준다. 창은 누르는 요소가 아니라 tabindex=-1 이다(Tab 순서에는 들지 않는다).
const focusDialog = (dialogEl, target) => {
    const el = target || dialogEl
    if (!el.matches(FOCUSABLE) && !el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1")
    el.focus({preventScroll: true})
}

/**
 * bootstrap 모달에 간편모드 대화상자(EasyShortcutHelp·DeviceDialog)와 같은 접근성 처리를 붙인다.
 *
 *  - 열기 직전 포커스를 기억하고, 닫히면 그 자리로 되돌린다. 코드로 Modal.show() 를 부르면
 *    bootstrap 은 포커스를 되돌려 주지 않아, 닫은 뒤 포커스가 body 로 떨어진다.
 *  - 열린 뒤 창 자체에 포커스를 두고 제목은 창 이름으로 한 번만 읽힌다(nameDialogByTitle). 예전에는 제목에 포커스를 둬
 *    제목(초점) + 창 이름(같은 제목)으로 두 번 들렸다. 대화상자가 스스로 포커스를 옮겼으면(입력창 등) 건드리지 않는다.
 *  - 열린 동안 배경을 스크린리더에서 감춘다(a11yModal). 포커스를 옮긴 뒤에 감춘다 — 반대 순서면
 *    포커스가 감춰진 영역에 남는 순간이 생겨 발화가 꼬인다.
 *  - 안내 영역을 미리 만들어 둔다. 열린 동안 liveAnnouncer 가 이 안에서 읽는다.
 *
 * 닫을 때 v-if 로 요소를 바로 지우는 모달(StaticModal 계열)은 hidden 이벤트가 오지 않을 수 있어,
 * 되돌리기는 hide(닫기 시작) 에서 한다.
 *
 * @param {HTMLElement} modalEl class="modal" 요소
 * @param {{initialFocus?: () => HTMLElement|null}} [options] 창 대신 처음 포커스를 줄 요소(사용자가 정한 예외만)
 * @returns {Function} 붙인 처리를 떼는 함수
 */
export function attachModalA11y(modalEl, {initialFocus} = {}) {
    if (!modalEl || modalEl.__a11yDetach) return modalEl?.__a11yDetach || (() => {})

    let returnTo = null
    let restoreBackground = null
    let shownTimer = null
    let generation = 0
    // 닫을 때 걸어 둔 「연 자리로 되돌리기」. 떼기(detach)가 먼저 와도 버리지 않고 그 자리에서 한다.
    // StaticModal·OrganismStaticModal 등은 닫기 리스너를 먼저 달고 거기서 setTimeout(detach, 0) 을 해, 떼기가 되돌리기보다 먼저 돌았다.
    // 그때 세대 검사에 걸려 되돌리기를 건너뛰어 닫으면 포커스가 body 로 떨어졌다(11/04 점검).
    let pendingReturn = null

    const onShow = () => {
        generation += 1
        pendingReturn = null
        const active = document.activeElement
        returnTo = active && active !== document.body && !modalEl.contains(active) ? active : null
        prepareLiveRegions(modalEl)
    }

    const onShown = () => {
        // 대화상자 자신의 shown 처리(입력창 포커스 등)가 끝난 뒤에 본다.
        const shownGeneration = generation
        clearTimeout(shownTimer)
        shownTimer = setTimeout(() => {
            if (shownGeneration !== generation) return
            if (!modalEl.isConnected) return
            const active = document.activeElement
            const ownFocusDone = active && active !== modalEl && modalEl.contains(active)
            nameDialogByTitle(modalEl)
            if (!ownFocusDone) focusDialog(modalEl, initialFocus?.())
            restoreBackground?.()
            restoreBackground = hideBackgroundFor(modalEl)
        }, 0)
    }

    const onHide = () => {
        generation += 1
        clearTimeout(shownTimer)
        restoreBackground?.()
        restoreBackground = null
        const target = returnTo
        returnTo = null
        const hiddenGeneration = generation
        const giveBack = () => {
            if (pendingReturn !== giveBack) return
            pendingReturn = null
            const active = document.activeElement
            const focusLost = !active || active === document.body || modalEl.contains(active) || !active.isConnected
            if (focusLost && target?.isConnected) target.focus({preventScroll: true})
        }
        pendingReturn = giveBack
        // bootstrap 이 포커스 가두기를 푼 뒤에 옮긴다.
        setTimeout(() => {
            if (hiddenGeneration !== generation) return
            giveBack()
        }, 0)
    }

    modalEl.addEventListener("show.bs.modal", onShow)
    modalEl.addEventListener("shown.bs.modal", onShown)
    modalEl.addEventListener("hide.bs.modal", onHide)

    const detach = () => {
        generation += 1
        clearTimeout(shownTimer)
        modalEl.removeEventListener("show.bs.modal", onShow)
        modalEl.removeEventListener("shown.bs.modal", onShown)
        modalEl.removeEventListener("hide.bs.modal", onHide)
        restoreBackground?.()
        restoreBackground = null
        delete modalEl.__a11yDetach
        // 닫는 중에 뗐으면 기다리던 되돌리기를 지금 한다. bootstrap 은 hide 이벤트 바로 뒤 가두기를 풀어 두었다.
        pendingReturn?.()
    }
    modalEl.__a11yDetach = detach
    return detach
}

/**
 * bootstrap 이벤트 없이 마운트되는 순간 보이는 대화상자(v-if + d-block 등)에 같은 처리를 켠다.
 * 마운트 직후(mounted/nextTick) 부르고, 돌려받은 함수를 해제 때(beforeUnmount) 부른다.
 *
 * @param {HTMLElement} dialogEl role="dialog" 요소
 * @param {{initialFocus?: () => HTMLElement|null, returnFocus?: HTMLElement|null}} [options]
 *   returnFocus 를 안 주면 켜는 순간의 포커스로 되돌린다.
 * @returns {Function} 끄는 함수(배경 되돌리기 + 포커스 복귀). 여러 번 불러도 안전하다.
 */
export function activateDialogA11y(dialogEl, {initialFocus, returnFocus} = {}) {
    if (!dialogEl) return () => {}
    const active = document.activeElement
    const returnTo = returnFocus !== undefined ? returnFocus
        : (active && active !== document.body && !dialogEl.contains(active) ? active : null)
    prepareLiveRegions(dialogEl)

    const focusInside = dialogEl.contains(document.activeElement) && document.activeElement !== dialogEl
    // 창 자체에 포커스, 제목은 창 이름으로 한 번(attachModalA11y 와 같다, ACCESSIBILITY.md 1).
    nameDialogByTitle(dialogEl)
    if (!focusInside) focusDialog(dialogEl, initialFocus?.())
    const restoreBackground = hideBackgroundFor(dialogEl)

    // bootstrap 모달과 달리 포커스 가두기가 없어, Tab 으로 감춰진 배경(aria-hidden)까지 나간다.
    // 대화상자 밖으로 나간 포커스는 안으로 되돌린다. 위에 새로 뜬 다른 대화상자·토스트는 그대로 둔다.
    const onFocusIn = event => {
        const target = event.target
        if (!dialogEl.isConnected || dialogEl.contains(target)) return
        if (target.closest?.('[role="dialog"], [role="alertdialog"], [aria-modal="true"], .swal2-container')) return
        const first = dialogEl.querySelector(FOCUSABLE) || dialogEl
        if (!first.matches(FOCUSABLE) && !first.hasAttribute("tabindex")) first.setAttribute("tabindex", "-1")
        first.focus({preventScroll: true})
    }
    document.addEventListener("focusin", onFocusIn, true)

    let done = false
    return () => {
        if (done) return
        done = true
        document.removeEventListener("focusin", onFocusIn, true)
        restoreBackground()
        const now = document.activeElement
        const focusLost = !now || now === document.body || dialogEl.contains(now) || !now.isConnected
        if (focusLost && returnTo?.isConnected) {
            // 대화상자가 DOM 에서 빠진 뒤에 옮긴다. 빠지는 중인 요소에 포커스가 남으면 body 로 떨어진다.
            setTimeout(() => {
                const later = document.activeElement
                if (!later || later === document.body || dialogEl.contains(later) || !later.isConnected) {
                    if (returnTo.isConnected) returnTo.focus({preventScroll: true})
                }
            }, 0)
        }
    }
}
